import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, Chip, Stack, Button, Badge } from '@mui/material';
import { Visibility, ExpandMore } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import contribuenteApi from '../../api/contribuenteApi';
import documentoApi from '../../api/documentoApi';
import scadenzaApi from '../../api/scadenzaApi';
import filesApi from '../../api/filesApi';
import ConfirmDialog from '../../components/ConfirmDeleteModal';
import KpiCard from '../../components/KpiCard';
import InstallPWAButton from '../../components/InstallPWAButton';
import { scadenzaStatusMap } from '../../utils/scadenzaStatusMapper';
import PaymentIcon from '@mui/icons-material/Payment';
import { formatDate } from '../../utils/dateFormatter';

const extractErrorMessage = (error) => {
  if (!error) return 'Errore sconosciuto';
  if (error.response?.data) return JSON.stringify(error.response?.data);
  if (!error.response) {
    return "L'elaborazione richiede più tempo del previsto. Riprova.";
  }
  return error.message || JSON.stringify(error);
};

export default function DashboardContribuenteMobile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [contribuente, setContribuente] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [scadenzeProssime, setScadenzeProssime] = useState(0);
  const [scadenzeScadute, setScadenzeScadute] = useState(0);
  const [listscadenzeprossime, setListScadenzeProssime] = useState([]);
  const [listscadenzescadute, setListScadenzeScadute] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [err, setErr] = useState(null);
  const pageSize = 5; // per liste rapide
  useEffect(() => {
    fetchContribuente();
    fetchScadenze();
    fetchDocumenti();
  }, [id]);

  const fetchContribuente = async () => {
    try {
      const res = await contribuenteApi.get(id);
      setContribuente(res.data);
    } catch (err) {
      console.error("Errore caricamento contribuente:", err);
      setErr(extractErrorMessage(err));
    }
  };

  const fetchDocumenti = async () => {
    try {
      const res = await documentoApi.listbycontribuente(id, { page: 0, size: 100 });
      setDocumenti(res.data.content || []);
    } catch (err) {
      console.error("Errore caricamento documenti:", err);
      setErr(extractErrorMessage(err));
    }
  };

  const fetchScadenze = async () => {
    try {
      const res = await scadenzaApi.listbycontribuente(id, { page: 0, size: 1000 });
      const all = res.data.content || [];
      setScadenze(all);

      const today = new Date();
      today.setHours(0,0,0,0);

      const prossime = [];
      const scadute = [];

      all.forEach(s => {
        const dataScad = new Date(s.dataScadenza);
        dataScad.setHours(0,0,0,0);
        const diffGiorni = (dataScad - today) / (1000 * 60 * 60 * 24);

        if (diffGiorni < 0 && s.stato !== 'pagata') scadute.push(s);
        else if (diffGiorni >= 0 && diffGiorni <= 7 && s.stato !== 'pagata') prossime.push(s);
      });

      setListScadenzeScadute(scadute);
      setListScadenzeProssime(prossime);
      setScadenzeScadute(scadute.length);
      setScadenzeProssime(prossime.length);

    } catch (err) {
      console.error("Errore caricamento scadenze:", err);
      setErr(extractErrorMessage(err));
    }
  };

  const openPdf = async (filename) => {
    try {
      const response = await filesApi.getfile(filename);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Impossibile aprire il PDF");
      setErr(extractErrorMessage(err));
    }
  };

  const confirmDelete = (id, type) => {
    setSelectedId(id);
    setDeleteType(type);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    try {
      if (deleteType === 'doc') await documentoApi.delete(selectedId);
      else if (deleteType === 'scad') await scadenzaApi.delete(selectedId);
      fetchDocumenti();
      fetchScadenze();
    } catch (err) {
      console.error("Errore eliminazione:", err);
      setErr(extractErrorMessage(err));
    } finally {
      setOpenDelete(false);
      setSelectedId(null);
      setDeleteType('');
    }
  };

  if (!contribuente) return <div>Loading...</div>;

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">👋 {contribuente.nome} {contribuente.cognome}</Typography>
        <Typography variant="body2" color="text.secondary">CF: {contribuente.codiceFiscale}</Typography>
      </Box>

      <Stack spacing={2} sx={{ px: 2 }}>
        <KpiCard title="Documenti" value={documenti.length} />
        <KpiCard title="Scadenze prossime" value={scadenzeProssime} color="warning" />
        <KpiCard title="Scadenze scadute" value={scadenzeScadute} color="error" />
      </Stack>

      <Typography variant="subtitle1" sx={{ mt: 3, px: 2 }}>Documenti recenti</Typography>
      <Stack spacing={1} sx={{ px: 2 }}>
        {documenti.map(d => (
         <Accordion key={d.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography>{d.tipo} {d.descrizione}</Typography>
                {d.rateSaltate > 0 && <Badge color="error" badgeContent={d.rateSaltate}><span /></Badge>}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">{d.contribuente?.nome} {d.contribuente?.cognome}</Typography>
              <Typography variant="body2">Data creazione: {formatDate(d.dataCreazione)}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={() => navigate(`/documenti/${d.id}`)}>Dettaglio</Button>
                <Button size="small" variant="outlined" onClick={() => navigate(`/documenti/${d.id}/edit`)}>Modifica</Button>
                <Button size="small" variant="outlined" onClick={() => confirmDelete(d.id,'doc')}>Elimina</Button>
                {d.pdfPath && <Button size="small" onClick={() => openPdf(d.pdfPath)}>PDF</Button>}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Typography variant="subtitle1" sx={{ mt: 3, px: 2, color: 'error.main' }}>Scadenze scadute  (le prime 5 partendo dalle più vecchie)</Typography>
      <Stack spacing={1} sx={{ px: 2 }}>
           {listscadenzescadute.slice(0, pageSize).map(s => (
          <Accordion key={s.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>{s.tipo} - {formatDate(s.dataScadenza)} - €{s.importo}</Typography>
                <Chip
                  label={scadenzaStatusMap[s.stato]?.label || s.stato}
                  color={scadenzaStatusMap[s.stato]?.color || 'default'}
                  size="small"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Button size="small" onClick={() => navigate(`/scadenze/${s.id}`)}>Dettaglio</Button>
                {s.pdfPath && (
                  <Button
                    startIcon={<Visibility />}
                    size="small"
                    onClick={() => openPdf(s.pdfPath, doc.contribuente?.id)}
                  >
                    Apri PDF
                  </Button>
                )}
                {s.stato !== "pagata" && (
                  <Button
                    size="small"
                    startIcon={<PaymentIcon />}
                    onClick={async () => {
                      try {
                        await scadenzaApi.setpagatamanuale(s.id);
                        fetchScadenze();
                      } catch (err) {
                        console.error('Errore marcatura manuale pagamento:', err);
                      }
                    }}
                  >
                    Marca Pagata
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Typography variant="subtitle1" sx={{ mt: 3, px: 2, color: 'warning.main' }}>Scadenze prossime  (le prime 5 partendo dalle più vecchie)</Typography>
      <Stack spacing={1} sx={{ px: 2 }}>
        {listscadenzeprossime.slice(0, pageSize).map(s => (
          <Card key={s.id}>
            <CardContent>
              <Typography>{s.tipo}</Typography>
              <Typography variant="caption">Scadenza: {formatDate(s.dataScadenza)}</Typography>
              <Typography fontWeight="bold">€ {s.importo}</Typography>
              <Button size="small" sx={{ mt: 1 }} onClick={() => navigate(`/scadenze/${s.id}`)}>Dettaglio</Button>
            </CardContent>
          </Card>
        ))}
      </Stack>
      <ConfirmDialog
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Conferma Eliminazione"
        message="Sei sicuro di voler eliminare questo elemento?"
      />
    </>
  );
}